import { useState, useEffect } from 'react';
import type { ProductData } from '../types/productTypes';
import type { GalleryMedia } from '../components/CameraGalleryData';
import { shopifyProductToComparisonData } from '../utils/shopifyDataTransformers';
import type { ShopifyProduct } from '../utils/shopifyDataTransformers';
import { generateCameraGalleryData } from '../utils/cameraGalleryTransformers';
import { warn } from '../utils/debug';

interface UseSelectedProductsResult {
  selectedProductIds: string[];
  setSelectedProductIds: React.Dispatch<React.SetStateAction<string[]>>;
  productData: (ProductData | null)[];
  galleryData: GalleryMedia[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Custom hook to manage selected products and their data
 */
export function useSelectedProducts(
  _initialSelectedProductIds: string[] = [],
  _maxProducts: number = 3,
  shopifyProductsData: ShopifyProduct[]
): UseSelectedProductsResult {
  // State for selected product IDs - always start empty for progressive disclosure
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  
  // State for processed product data
  const [productData, setProductData] = useState<(ProductData | null)[]>([]);
  const [galleryData, setGalleryData] = useState<GalleryMedia[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Process selected products whenever they change or shopify data changes
  useEffect(() => {
    const processSelectedProducts = async () => {
      if (selectedProductIds.length === 0 || shopifyProductsData.length === 0) return;

      setIsLoading(true);
      setError(null);

      try {
        // Convert selected products to comparison data format, preserving array positions
        const comparisonData = selectedProductIds.map(handle => {
          // Skip empty slots
          if (!handle || typeof handle !== 'string') {
            return null;
          }
          // Find the Shopify product by handle
          const product = shopifyProductsData.find(product => product && product.handle === handle);

          if (!product) {
            warn(`Product with handle ${handle} not found in Shopify data`);
            // Return fallback data if product not found
            return {
              name: 'Product Not Found',
              image: 'https://placehold.co/150x250/D3D3D3/000000?text=NotFound',
              url: '',
              colorOptions: [{ name: 'Default', color: 'gray', isActive: true }],
              price: [{ storage: 'N/A', price: 'N/A' }],
              easeOfUseScore: 'N/A',
              navigation: 'N/A',
              iosSupport: { releaseYear: 'N/A', latestIOS: 'N/A', yearsUntilEndOfSupport: 'N/A' },
              phoneSize: 'N/A',
              screenSize: 'N/A',
              batteryLife: 'N/A',
              camera: { 
                photoUrl: 'https://placehold.co/100x60/EEEEEE/000?text=NA', 
                photoUrls: [],
                videoUrl: 'https://placehold.co/100x60/EEEEEE/000?text=NA',
                videoSrc: ''
              },
              cameraSpecs: {
                frontCamera: 'N/A',
                rearCamera: 'N/A',
                slowMotionVideo: 'N/A',
                opticalZoom: 'N/A'
              },
              processor: 'N/A',
              storage: ['N/A'],
              sdCardCompatible: 'No',
              appleWatchSupport: 'No',
              appleWatchSESupport: 'No',
              appleWatchUltraSupport: 'No',
              charging: ['N/A'],
              connectivity: ['N/A']
            } as ProductData;
          }

          // Get model name from metafields if available, otherwise use product title
          // let modelName = product.title; // Unused variable
          if (product.metafields && Array.isArray(product.metafields)) {
            const modelField = product.metafields.find(
              field => field && field.key && field.namespace &&
                typeof field.key === 'string' &&
                field.key.toLowerCase() === 'model' &&
                (field.namespace === 'specifications' || field.namespace === 'custom')
            );

            if (modelField && modelField.value) {
              try {
                const parsedValue = JSON.parse(modelField.value);
                if (Array.isArray(parsedValue) && parsedValue.length > 0) {
                  // modelName = parsedValue[0]; // Unused variable
                } else {
                  // modelName = modelField.value; // Unused variable
                }
              } catch (e) {
                // If not valid JSON, use the value directly
                // modelName = modelField.value; // Unused variable
              }
            }
          }
          
          // Log basic product info and relevant camera metafields
          
          // Log camera-related metafields only
          if (product.metafields && Array.isArray(product.metafields)) {
            // const _cameraMetafields = product.metafields.filter(field => 
            //   field && field.namespace && field.key && 
            //   (field.key.toLowerCase().includes('landscape') || 
            //    field.key.toLowerCase().includes('close') || 
            //    field.key.toLowerCase().includes('light') ||
            //    field.key.toLowerCase() === 'camera')
            // ); // Unused variable
            
          }

          // Convert the Shopify product data to our comparison format
          return shopifyProductToComparisonData(product);
        });

        setProductData(comparisonData);

        // Generate camera gallery data with validated product IDs
        try {
          const validProductIds = selectedProductIds.filter(handle => handle && typeof handle === 'string');
          const cameraData = generateCameraGalleryData(shopifyProductsData, validProductIds);
          
          
          setGalleryData(cameraData);
        } catch (galleryError) {
          console.error('Failed to generate camera gallery data', galleryError);
          // Don't fail the whole operation if gallery data fails
        }
      } catch (e) {
        console.error('Failed to process product details', e);
        setError('Failed to process product details.');
      } finally {
        setIsLoading(false);
      }
    };

    processSelectedProducts();
  }, [selectedProductIds, shopifyProductsData]);

  return {
    selectedProductIds,
    setSelectedProductIds,
    productData,
    galleryData,
    isLoading,
    error
  };
}